import { onCLS, onFCP, onLCP, onTTFB, onINP, Metric } from 'web-vitals';
import { getFirstConsistentlyInteractive } from '@airwallex/tti-polyfill';
import { airTracker } from './airTracker';
import { isPerformanceAPISupported, navStart } from '@/constants/performance';
import { trace } from './trace';

export interface PerformanceMetric {
  name: string;
  value: number;
  rating: 'good' | 'needs-improvement' | 'poor';
  delta: number;
  id: string;
  navigationType: string;
}

export interface WebVitalsData {
  fcp?: PerformanceMetric;
  lcp?: PerformanceMetric;
  cls?: PerformanceMetric;
  ttfb?: PerformanceMetric;
  inp?: PerformanceMetric;
}

class WebVitalsPerformance {
  private metrics: WebVitalsData = {};
  private callbacks: Array<(data: WebVitalsData) => void> = [];

  constructor() {
    this.initializeWebVitals();
  }

  private initializeWebVitals() {
    // First Contentful Paint
    onFCP((metric: Metric) => {
      this.metrics.fcp = metric as PerformanceMetric;
      trace.mark('WEB_VITALS_FCP', {
        value: metric.value,
        timeElapsed: metric.value,
        rating: metric.rating,
      });
      this.notifyCallbacks();
    });

    // Largest Contentful Paint
    onLCP((metric: Metric) => {
      this.metrics.lcp = metric as PerformanceMetric;
      trace.mark('WEB_VITALS_LCP', {
        value: metric.value,
        timeElapsed: metric.value,
        rating: metric.rating,
      });
      this.notifyCallbacks();
    });

    // Cumulative Layout Shift
    onCLS((metric: Metric) => {
      this.metrics.cls = metric as PerformanceMetric;
      trace.mark('WEB_VITALS_CLS', {
        value: metric.value,
        timeElapsed: metric.value,
        rating: metric.rating,
      });
      this.notifyCallbacks();
    });

    // Time to First Byte
    onTTFB((metric: Metric) => {
      this.metrics.ttfb = metric as PerformanceMetric;
      trace.mark('WEB_VITALS_TTFB', {
        value: metric.value,
        timeElapsed: metric.value,
        rating: metric.rating,
      });
      this.notifyCallbacks();
    });

    // Interaction to Next Paint
    onINP((metric: Metric) => {
      this.metrics.inp = metric as PerformanceMetric;
      trace.mark('WEB_VITALS_INP', {
        value: metric.value,
        timeElapsed: metric.value,
        rating: metric.rating,
      });
      this.notifyCallbacks();
    });
  }

  private notifyCallbacks() {
    this.callbacks.forEach((callback) => callback(this.metrics));
  }

  public onMetric(callback: (data: WebVitalsData) => void) {
    this.callbacks.push(callback);

    // Call immediately if we already have some metrics
    if (Object.keys(this.metrics).length > 0) {
      callback(this.metrics);
    }
  }

  public getMetrics(): WebVitalsData {
    return { ...this.metrics };
  }

  public logMetrics() {
    airTracker.info('WEB_VITALS_SUMMARY', {
      metrics: this.metrics,
    });
  }
}

// Create singleton instance
const webVitalsPerformance = new WebVitalsPerformance();

function calculateDCL() {
  if (!isPerformanceAPISupported) return;

  const logger = () => {
    const [nav] = performance.getEntriesByType('navigation');
    const value = Math.max(nav.domContentLoadedEventEnd - navStart, 0);
    trace.mark('WEB_TIMING_DCL', { value, timeElapsed: value });
  };
  try {
    if (document.readyState === 'interactive' || document.readyState === 'complete') {
      logger();
    } else {
      document.addEventListener('DOMContentLoaded', logger);
    }
  } catch (error) {
    console.warn('DCL measurement failed:', error);
  }
}

// tti
// google suggest use INF instead of TTI
async function calculateTTI(whitelist: string[]) {
  try {
    const tti = await getFirstConsistentlyInteractive({
      networkWhitelist: whitelist,
    });
    trace.mark('WEB_VITALS_TTI', {
      timeElapsed: tti,
      value: tti,
    });
  } catch (error) {
    console.warn('TTI measurement failed:', error);
  }
}

export const logPerformanceMetrics = (whitelist: string[] = []) => {
  webVitalsPerformance.logMetrics();
  calculateTTI(whitelist);
  calculateDCL();
};
